import React, { FC, useEffect, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import Button from "@/components/Button";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import Grid from "@mui/material/Grid2";
import { FormHelperText, TextField, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useCreateOnDemandIncentive } from "@/hooks/useEnrollments";
import { Enrollment } from "@/domain/enrollment.interface";
import { useCurrencySetting } from "@/hooks/useSetting";
import AlertDialog from "@/components/AlertDialog";
import { useProgramBudget } from "@/hooks/usePrograms";

type OnDemandIncentiveModalProps = {
  open: boolean;
  onClose: () => void;
  participantName?: string;
  enrollment?: Enrollment;
};

const OnDemandIncentiveModal: FC<OnDemandIncentiveModalProps> = ({
  open,
  onClose,
  participantName,
  enrollment,
}) => {
  const { t } = useTranslation("clients");
  const { t: tCommon } = useTranslation("common");
  const { userInfo } = useUserStore();
  const { showSnackBar } = useFeedBack();
  const theme = useAppTheme();
  const { participantId } = useParams();
  const [amount, setAmount] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>();
  const currency = useCurrencySetting();
  const [genericAlert, setGenericAlert] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const createOnDemandIncentive = useCreateOnDemandIncentive();

  // Fetch program budget data
  const programId = enrollment?.programId ?? enrollment?.program?._id ?? "";
  const { budgetData, isLoadingBudget, refetchBudget } = useProgramBudget(
    programId,
    open,
  );

  const handleConfirm = () => {
    if (!amount) {
      setAmountError(tCommon("validations.required"));
      return;
    } else if (!/^\d*(\.\d{0,2})?$/.test(amount)) {
      setAmountError(
        t("client_profile.on-demand-incentive-amount-error-format"),
      );
      return;
    } else {
      setAmountError("");
    }

    // Prevent validation if budget data is still loading
    if (isLoadingBudget) {
      setAmountError(t("client_profile.on-demand-incentive-budget-loading"));
      return;
    }

    const amountNumber = Number(amount);
    if (
      enrollment?.program?.parameters?.minAmount &&
      enrollment?.program?.parameters?.maxAmount
    ) {
      if (
        amountNumber < enrollment.program.parameters.minAmount ||
        amountNumber > enrollment.program.parameters.maxAmount
      ) {
        setAmountError(
          t("client_profile.on-demand-incentive-out-of-range", {
            currency: currency?.currency,
            minimum: enrollment?.program?.parameters?.minAmount,
            maximum: enrollment?.program?.parameters?.maxAmount,
          }),
        );
        return;
      }
    }
    // Validate budget if program has budget limit
    if (
      budgetData?.hasBudgetLimit &&
      budgetData?.remainingBudget !== null &&
      budgetData?.remainingBudget !== undefined
    ) {
      if (amountNumber > budgetData.remainingBudget) {
        setAmountError(
          t("client_profile.on-demand-incentive-insufficient-budget", {
            currency: currency?.currency,
            remaining: budgetData.remainingBudget.toFixed(2),
          }),
        );
        return;
      }
    }
    setGenericAlert({
      open: true,
      title: t("client_profile.on-demand-incentive-confirmation-title"),
      message: t("client_profile.on-demand-incentive-confirmation-message"),
      onConfirm: handleSubmit,
    });
  };

  const handleSubmit = async () => {
    setGenericAlert({
      open: false,
      title: "",
      message: "",
      onConfirm: () => {},
    });

    const payload = {
      participantId: participantId as string,
      enrollmentId: enrollment?._id ?? "",
      createdBy: userInfo?._id as string,
      amount: Number(amount),
    };

    await createOnDemandIncentive.mutateAsync(payload, {
      onSuccess: () => {
        // Invalidate budget cache to reflect updated remaining budget
        refetchBudget();
        onClose();
        showSnackBar(
          t("client_profile.on-demand-incentive-success"),
          "success",
        );
      },
      onError: (error: ErrorResponse) => {
        const message = Array.isArray(error.message)
          ? error.message[0]
          : error.message;
        showSnackBar(
          message || t("client_profile.on-demand-incentive-error"),
          "error",
        );
      },
    });
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        isPending={false}
        title={t("client_profile.on-demand-incentive-title")}
        theme={theme}
        size={{
          width: "100%",
          minHeight: 300,
          maxHeight: 400,
          maxWidth: 380,
        }}
        content={
          <Grid container spacing={2} mt={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="bodyLarge">
                {t("client_profile.on-demand-incentive-message", {
                  currency: currency?.currency,
                  minimum: enrollment?.program?.parameters?.minAmount,
                  maximum: enrollment?.program?.parameters?.maxAmount,
                })}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label={tCommon("amount")}
                variant="outlined"
                value={amount}
                onChange={(value) => {
                  const inputValue = value.target.value;
                  setAmount(inputValue);
                  setAmountError("");
                }}
                error={!!amountError}
                fullWidth
                required={true}
                type="number"
              />
              {amountError && (
                <FormHelperText className="px-3.5 pt-0.5 !text-error-main">
                  {amountError}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        }
        actions={
          <Box
            sx={{
              display: "flex",
              gap: "10px",
            }}
          >
            <Button
              variantType="cancel"
              onClick={onClose}
              disabled={false}
              label={tCommon("cancel")}
            />
            <Button
              variantType="save"
              onClick={handleConfirm}
              disabled={createOnDemandIncentive.isPending || isLoadingBudget}
              isLoading={createOnDemandIncentive.isPending || isLoadingBudget}
              label={tCommon("send")}
            />
          </Box>
        }
      />
      {genericAlert.open && (
        <AlertDialog
          open={genericAlert.open}
          onCancel={() =>
            setGenericAlert((prevState) => ({ ...prevState, open: false }))
          }
          onConfirm={() => {
            genericAlert.onConfirm();
          }}
          title={genericAlert.title}
          description={genericAlert.message}
          confirmText="Confirm"
          cancelText="Cancel"
        />
      )}
    </>
  );
};

export default OnDemandIncentiveModal;
